import * as React from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { SimpleNotification } from "@/hooks/useNotifications";
import { NotificationCategory, CATEGORY_INFO } from "@/utils/notificationSeverity";
import SwipeableNotificationItem from "./SwipeableNotificationItem";
import NotificationItem from "./NotificationItem";

interface NotificationAccordionGroupProps {
  category: NotificationCategory;
  notifications: SimpleNotification[];
  unreadCount: number;
  isOpen: boolean;
  onToggle: () => void;
  onDismiss: (id: string) => void;
  onMarkAsRead: (id: string) => void;
  isMobile: boolean;
}

export const NotificationAccordionGroup: React.FC<NotificationAccordionGroupProps> = ({
  category,
  notifications,
  unreadCount,
  isOpen,
  onToggle,
  onDismiss,
  onMarkAsRead,
  isMobile,
}) => {
  const categoryInfo = CATEGORY_INFO[category];
  const totalCount = notifications.length;

  if (totalCount === 0) return null;

  return (
    <div className="mb-2">
      {/* Header */}
      <button
        onClick={onToggle}
        className={cn(
          "w-full flex items-center justify-between p-3 rounded-lg transition-all duration-200",
          "bg-white/5 hover:bg-white/10 border border-white/10",
          isOpen && "bg-white/10 border-white/20"
        )}
      >
        <div className="flex items-center gap-3">
          <span className="text-lg">{categoryInfo.icon}</span>
          <span className="font-medium text-white">{categoryInfo.label}</span>
          <span className="text-sm text-white/50">({totalCount})</span>
          {unreadCount > 0 && (
            <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold bg-red-500 text-white rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <ChevronDown 
          className={cn(
            "h-5 w-5 text-white/60 transition-transform duration-200",
            isOpen && "rotate-180"
          )} 
        />
      </button>

      {/* Content */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pt-2 space-y-2">
              {notifications.map((notification) => {
                // Convert SimpleNotification to Notification format expected by NotificationItem
                const notificationProps = {
                  id: notification.id,
                  title: notification.title,
                  message: notification.message,
                  timestamp: new Date(notification.created_at),
                  read: notification.is_read,
                  type: notification.notification_type as any,
                  userId: notification.source_id || undefined,
                  userAvatar: notification.user_avatar || undefined,
                  username: notification.username || undefined,
                };

                const itemContent = (
                  <NotificationItem
                    notification={notificationProps}
                    onDismiss={onDismiss}
                    onMarkAsRead={onMarkAsRead}
                    isDbNotification
                  />
                );

                if (isMobile) {
                  return (
                    <SwipeableNotificationItem
                      key={notification.id}
                      onSwipeLeft={() => onDismiss(notification.id)}
                      onSwipeRight={() => onMarkAsRead(notification.id)}
                      isRead={notification.is_read}
                    >
                      {itemContent}
                    </SwipeableNotificationItem>
                  );
                }

                return <div key={notification.id}>{itemContent}</div>;
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
